import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import Skeleton from 'react-loading-skeleton';
import { Paginator } from 'primereact/paginator';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { handleApiResponse } from '../../utilities/apiUtils';

import '../../styles/emailTemplates.css';

// TODO: Re-enable in a later phase
const SHOW_FIELD_TOKENS = false;

const FIELD_SECTIONS = [
  {
    label: 'Insured Fields',
    fields: [
      { name: 'Name', token: '{Insured.Name}' },
      { name: 'DBA', token: '{Insured.DBA}' },
      { name: 'Address line 1', token: '{Insured.AddressLine1}' },
      { name: 'Address line 2', token: '{Insured.AddressLine2}' },
      { name: 'Street Address', token: '{Insured.StreetAddress}' },
      { name: 'State Name', token: '{Insured.StateName}' },
      { name: 'State Abbreviation', token: '{Insured.StateAbbr}' },
      { name: 'City, State, ZIP', token: '{Insured.CityStateZip}' },
      { name: 'First Name', token: '{Insured.PersonalType_FirstName}' },
      { name: 'Last Name', token: '{Insured.PersonalType_LastName}' },
      { name: 'Phone', token: '{Insured.Phone}' },
      { name: 'Cell Phone', token: '{Insured.CellPhone}' },
      { name: 'Fax', token: '{Insured.Fax}' },
      { name: 'Email', token: '{Insured.Email}' },
      { name: 'Type of Business', token: '{Insured.TypeOfBusiness}' },
    ],
  },
  {
    label: 'Agency Fields',
    fields: [
      { name: 'Name', token: '{InsuranceAgency.Name}' },
      { name: 'Address line 1', token: '{InsuranceAgency.AddressLine1}' },
      { name: 'Address line 2', token: '{InsuranceAgency.AddressLine2}' },
      { name: 'Phone', token: '{InsuranceAgency.Phone}' },
      { name: 'Cell Phone', token: '{InsuranceAgency.CellPhone}' },
      { name: 'Fax', token: '{InsuranceAgency.Fax}' },
      { name: 'Email', token: '{InsuranceAgency.EMail}' },
      { name: 'ZIP Code', token: '{InsuranceAgency.ZipCode}' },
      { name: 'City', token: '{InsuranceAgency.City}' },
      { name: 'State', token: '{InsuranceAgency.MailingStateAbbr}' },
    ],
  },
  {
    label: 'Agent Fields',
    fields: [
      { name: 'Full Name', token: '{Agent.FullName}' },
      { name: 'First Name', token: '{Agent.FirstName}' },
      { name: 'Last Name', token: '{Agent.LastName}' },
      { name: 'Phone', token: '{Agent.Phone}' },
      { name: 'Email', token: '{Agent.EMail}' },
      { name: 'Cell Phone', token: '{Agent.CellPhone}' },
      { name: 'Fax', token: '{Agent.Fax}' },
    ],
  },
  {
    label: 'CSR Fields',
    fields: [
      { name: 'Full Name', token: '{CSR.FullName}' },
      { name: 'First Name', token: '{CSR.FirstName}' },
      { name: 'Last Name', token: '{CSR.LastName}' },
      { name: 'Phone', token: '{CSR.Phone}' },
      { name: 'Email', token: '{CSR.EMail}' },
      { name: 'Cell Phone', token: '{CSR.CellPhone}' },
      { name: 'Fax', token: '{CSR.Fax}' },
    ],
  },
  {
    label: 'Policy Fields',
    fields: [
      { name: 'Number', token: '{Policy.Number}' },
      { name: 'Effective Date', token: '{Policy.EffectiveDate}' },
      { name: 'Expiration Date', token: '{Policy.ExpirationDate}' },
      { name: 'Status', token: '{Policy.StatusExcel}' },
      { name: 'Line of Business', token: '{Policy.LinesOfBusinessString}' },
      { name: 'Insured', token: '{Policy.InsuredExcel}' },
    ],
  },
  {
    label: 'Other Fields',
    fields: [
      { name: 'Today\'s Date', token: '{Util.TodayDate}' },
    ],
  },
];

const TEMPLATE_CATEGORIES = [
  { label: 'Email', value: 'Email' },
  { label: 'Certificates', value: 'Certificates' },
  { label: 'Note', value: 'SMS' },
];

const FILTER_CATEGORIES = [
  { label: 'All Categories', value: '' },
  { label: 'Email', value: 'Email' },
  { label: 'Certificates', value: 'Certificates' },
  { label: 'Note', value: 'SMS' },
];

type Template = {
  id: string;
  name: string;
  templateCategory: string;
  subject?: string;
  body?: string;
  isPrivate?: boolean;
  createDate?: string;
};

export default function EmailTemplatesPage() {
  const toastRef = useRef<Toast>(null);
  const navigate = useNavigate();

  // View state
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  // List state
  const [templates, setTemplates] = useState<Template[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [body, setBody] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce search — wait 400 ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Server-side fetch — triggered by page, category, or debounced search
  const fetchTemplates = useCallback(async (pageNum = 0, category = categoryFilter, search = debouncedSearch) => {
    setLoading(true);
    setError(null);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const params = new URLSearchParams({
        top: String(rowsPerPage),
        skip: String(pageNum * rowsPerPage),
      });
      if (category) params.set('category', category);
      if (search)   params.set('search', search);
      const response = await fetch(
        `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-templates?${params.toString()}`,
        {
          headers: {
            'NCToken': ncToken,
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );
      await handleApiResponse(response);
      const data = await response.json();
      setTotalRecords(data['@odata.count'] ?? 0);
      setTemplates((data.value || []).map((item: any) => ({
        id: item.id || item.databaseId,
        name: item.name || '',
        templateCategory: (item.templateCategory || '').trim(),
        subject: item.subject || '',
        body: item.body || '',
        isPrivate: item.private || false,
        createDate: item.createDate || '',
      })));
    } catch (err: any) {
      setError(err.message || 'Error fetching templates');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch whenever page, category, or debounced search changes
  useEffect(() => {
    fetchTemplates(page, categoryFilter, debouncedSearch);
  }, [page, categoryFilter, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset page when category changes
  useEffect(() => { setPage(0); }, [categoryFilter]);

  const toggleSection = (idx: number) => {
    setExpandedSections((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const insertField = (fieldName: string, token: string) => {
    setBody((prev) => prev + `${fieldName}: ${token}`);
    toastRef.current?.show({ severity: 'info', summary: 'Field Inserted', detail: `${fieldName}: ${token}`, life: 1500 });
  };

  const resetForm = () => {
    setName('');
    setCategory(null);
    setSubject('');
    setIsPrivate(false);
    setBody('');
    setExpandedSections({});
    setEditingTemplate(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setView('create');
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setName(template.name);
    setCategory(template.templateCategory);
    setSubject(template.subject || '');
    setIsPrivate(template.isPrivate || false);
    setBody(template.body || '');
    setView('edit');
  };

  const handleCancel = () => {
    resetForm();
    setView('list');
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toastRef.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Template name is required', life: 3000 });
      return;
    }
    if (!category) {
      toastRef.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please select a category', life: 3000 });
      return;
    }

    setSaving(true);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';

      const payload: Record<string, unknown> = {
        name,
        templateCategory: category,
        subject,
        body,
        isPrivateTemplate: isPrivate,
      };
      if (editingTemplate) {
        payload.id = editingTemplate.id;
      }

      const response = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/upsert-certificate-template`, {
        method: 'POST',
        headers: {
          'NCToken': ncToken,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      await handleApiResponse(response);

      toastRef.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: `Template ${editingTemplate ? 'updated' : 'created'} successfully`,
        life: 3000
      });

      resetForm();
      setView('list');
      setPage(0);
      fetchTemplates(0, categoryFilter, debouncedSearch);
    } catch (err: any) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to save template', life: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (template: Template) => {
    setDeleteTarget(template);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const res = await fetch(
        `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-templates/${deleteTarget.id}`,
        {
          method: 'DELETE',
          headers: {
            'NCToken': ncToken,
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );
      await handleApiResponse(res);
      toastRef.current?.show({ severity: 'success', summary: 'Deleted', detail: `"${deleteTarget.name}" was deleted`, life: 3000 });
      setDeleteTarget(null);
      setPage(0);
      fetchTemplates(0, categoryFilter, debouncedSearch);
    } catch (err: any) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to delete template', life: 3000 });
    } finally {
      setDeleting(false);
    }
  };

  const renderListView = () => (
    <div className="emailTpl__listPage">

      {/* Header */}
      <div className="emailTpl__listPageHeader">
        <div className="emailTpl__listPageHeaderLeft">
          <button className="emailTpl__backBtn" onClick={() => navigate('/dashboard')}>
            <i className="pi pi-arrow-left" />
          </button>
          <div>
            <h1 className="emailTpl__listPageTitle">Email Templates</h1>
            <p className="emailTpl__listPageSubtitle">
              {loading ? 'Loading…' : `${totalRecords} template${totalRecords !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <Button
          icon="pi pi-plus"
          label="Create Template"
          className="p-button-sm emailTpl__saveBtn"
          onClick={handleCreateNew}
        />
      </div>

      {/* Filter bar */}
      <div className="emailTpl__listFilters">
        <div className="emailTpl__listFilterField">
          <label className="emailTpl__listFilterLabel">Search</label>
          <span className="p-input-icon-left emailTpl__listSearchWrap">
            <i className="pi pi-search" />
            <InputText maxLength={100}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or subject…"
              className="emailTpl__listSearchInput"
            />
          </span>
        </div>
        <div className="emailTpl__listFilterField">
          <label className="emailTpl__listFilterLabel">Category</label>
          <Dropdown
            value={categoryFilter}
            options={FILTER_CATEGORIES}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setCategoryFilter(e.value ?? '')}
            placeholder="All categories"
            className="emailTpl__listCategoryDrop"
            emptyMessage="No available options"
          />
        </div>
      </div>

      {/* Table area */}
      <div className="emailTpl__tableWrap">
        {loading ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
                <th style={{ width: '30%', padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b' }}>Name</th>
                <th style={{ width: '14%', padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b' }}>Category</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b' }}>Subject</th>
                <th style={{ width: 100, padding: '10px 16px', fontWeight: 600, fontSize: 12, color: '#64748b' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '13px 16px' }}><Skeleton width={160} height={13} borderRadius={4} /></td>
                  <td style={{ padding: '13px 16px' }}><Skeleton width={75} height={22} borderRadius={20} /></td>
                  <td style={{ padding: '13px 16px' }}><Skeleton width={220} height={13} borderRadius={4} /></td>
                  <td style={{ padding: '13px 16px' }}><div style={{ display: 'flex', gap: 5 }}><Skeleton width={28} height={28} borderRadius={6} /><Skeleton width={28} height={28} borderRadius={6} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : error ? (
          <div className="emailTpl__error">
            <i className="pi pi-exclamation-circle" />
            <span>{error}</span>
          </div>
        ) : templates.length === 0 ? (
          <div className="emailTpl__empty">
            <i className="pi pi-file" />
            <p>{searchQuery || categoryFilter ? 'No templates match your search.' : 'No templates yet.'}</p>
            {!searchQuery && !categoryFilter && (
              <Button label="Create Your First Template" icon="pi pi-plus" className="p-button-sm emailTpl__saveBtn" style={{ marginTop: 12 }} onClick={handleCreateNew} />
            )}
          </div>
        ) : (
          <DataTable value={templates} className="emailTpl__dataTable" size="small">
            <Column
              field="name"
              header="Name"
              style={{ width: '30%' }}
              body={(r) => <span className="emailTpl__templateName">{r.name}</span>}
            />
            <Column
              field="templateCategory"
              header="Category"
              style={{ width: '14%' }}
              body={(r) => (
                <span className={`emailTpl__categoryBadge emailTpl__categoryBadge--${r.templateCategory.toLowerCase().replace(/[^a-z]/g, '')}`}>
                  {r.templateCategory}
                </span>
              )}
            />
            <Column
              field="subject"
              header="Subject"
              body={(r) => <span className="emailTpl__subjectCell" title={r.subject}>{r.subject || '—'}</span>}
            />
            <Column
              header="Actions"
              style={{ width: '100px' }}
              body={(r) => (
                <div className="emailTpl__actions">
                  <button className="emailTpl__actionBtn emailTpl__actionBtn--edit" title="Edit" onClick={() => handleEdit(r)}>
                    <i className="pi pi-pencil" />
                  </button>
                  <button className="emailTpl__actionBtn emailTpl__actionBtn--delete" title="Delete" onClick={() => handleDelete(r)}>
                    <i className="pi pi-trash" />
                  </button>
                </div>
              )}
            />
          </DataTable>
        )}
      </div>

      {/* Paginator */}
      {!loading && !error && totalRecords > rowsPerPage && (
        <div className="emailTpl__listPaginator">
          <Paginator
            rows={rowsPerPage}
            totalRecords={totalRecords}
            first={page * rowsPerPage}
            onPageChange={(e) => setPage(Math.floor(e.first / rowsPerPage))}
            template="PrevPageLink PageLinks NextPageLink"
            className="requests-paginator"
          />
        </div>
      )}
    </div>
  );

  // Render Form View (Create/Edit)
  const renderFormView = () => (
    <div className="emailTpl__formPage">
      {/* Page Header — title + back only */}
      <div className="emailTpl__formPageHeader">
        <div className="emailTpl__formPageHeaderLeft">
          <button className="emailTpl__backBtn" onClick={() => setView('list')}>
            <i className="pi pi-arrow-left" />
          </button>
          <div className="emailTpl__formPageTitleWrap">
            <span className="emailTpl__formBreadcrumb">Email Templates</span>
            <i className="pi pi-chevron-right emailTpl__formBreadcrumbSep" />
            <h1 className="emailTpl__formPageTitle">
              {view === 'edit' ? 'Edit Template' : 'New Template'}
            </h1>
          </div>
        </div>
      </div>

      {/* Two-column body */}
      <div className="emailTpl__formBody">

        {/* LEFT — form */}
        <div className="emailTpl__formLeft">
          <div className="emailTpl__formCard">
            <div className="emailTpl__formCardHeader">
              <i className="pi pi-file-edit emailTpl__formCardIcon" />
              <span>Template Details</span>
            </div>

            <div className="emailTpl__field">
              <label className="emailTpl__fieldLabel">Template Name <span className="emailTpl__req">*</span></label>
              <InputText maxLength={100}
                className="emailTpl__fieldInput"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Certificate request confirmation"
              />
            </div>

            <div className="emailTpl__fieldRow2">
              <div className="emailTpl__field">
                <label className="emailTpl__fieldLabel">Category <span className="emailTpl__req">*</span></label>
                <Dropdown
                  className="emailTpl__fieldInput"
                  value={category}
                  options={TEMPLATE_CATEGORIES}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => setCategory(e.value)}
                  placeholder="Select category"
                  showClear
                  emptyMessage="No available options"
                />
              </div>
              <div className="emailTpl__field">
                <label className="emailTpl__fieldLabel">Subject</label>
                <InputText maxLength={200}
                  className="emailTpl__fieldInput"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject line"
                />
              </div>
            </div>

            <div className="emailTpl__field emailTpl__field--grow">
              <label className="emailTpl__fieldLabel">Body</label>
              <InputTextarea maxLength={5000}
                className="emailTpl__fieldInput emailTpl__fieldTextarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                placeholder="Write your template content here. Click any token on the right to insert it."
              />
            </div>

            <div className="emailTpl__checkboxField">
              <Checkbox
                inputId="privateTemplate"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.checked ?? false)}
              />
              <label htmlFor="privateTemplate" className="emailTpl__checkboxFieldLabel">
                <span className="emailTpl__checkboxTitle">Private Template</span>
                <span className="emailTpl__checkboxDesc">Only visible to you, not shared with other users</span>
              </label>
            </div>
          </div>
        </div>

        {/* RIGHT — field reference panel (hidden until later phase; set SHOW_FIELD_TOKENS = true to re-enable) */}
        {SHOW_FIELD_TOKENS && (
          <div className="emailTpl__formRight">
            <div className="emailTpl__refPanel">
              <div className="emailTpl__refPanelHeader">
                <i className="pi pi-code emailTpl__refPanelIcon" />
                <div>
                  <div className="emailTpl__refPanelTitle">Field Tokens</div>
                  <div className="emailTpl__refPanelSub">Click to insert into body</div>
                </div>
              </div>
              <div className="emailTpl__refPanelList">
                {FIELD_SECTIONS.map((section, idx) => (
                  <div key={idx} className="emailTpl__refSection">
                    <button
                      type="button"
                      onClick={() => toggleSection(idx)}
                      className="emailTpl__refToggle"
                    >
                      <span className="emailTpl__refToggleLabel">{section.label}</span>
                      <i className={`pi ${expandedSections[idx] ? 'pi-chevron-up' : 'pi-chevron-down'} emailTpl__refToggleIcon`} />
                    </button>
                    {expandedSections[idx] && (
                      <div className="emailTpl__refTokenList">
                        {section.fields.map((field) => (
                          <div
                            key={field.token}
                            className="emailTpl__refToken"
                            onClick={() => insertField(field.name, field.token)}
                            title={`Insert: ${field.token}`}
                          >
                            <span className="emailTpl__refTokenName">{field.name}</span>
                            <code className="emailTpl__refTokenCode">{field.token}</code>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Footer — actions */}
      <div className="emailTpl__formFooter">
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-outlined p-button-sm emailTpl__cancelBtn"
          onClick={handleCancel}
          disabled={saving}
        />
        <Button
          label={saving ? 'Saving…' : view === 'edit' ? 'Save Changes' : 'Create Template'}
          icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          className="p-button-sm emailTpl__saveBtn"
          onClick={handleSave}
          disabled={saving}
        />
      </div>
    </div>
  );

  return (
    <div className="emailTpl__pageWrap">
      <Toast ref={toastRef} position="top-center" />
      <div className="emailTpl__card">
        {view === 'list' ? renderListView() : renderFormView()}
      </div>

      <Dialog
        visible={!!deleteTarget}
        onHide={() => { if (!deleting) setDeleteTarget(null); }}
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626', fontSize: 18 }} />
            <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827' }}>Delete Template</span>
          </div>
        }
        style={{ width: '420px' }}
        modal
        draggable={false}
        resizable={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '4px 0' }}>
            <Button
              label="Cancel"
              className="p-button-outlined p-button-sm emailTpl__cancelBtn"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            />
            <Button
              label={deleting ? 'Deleting…' : 'Delete'}
              icon={deleting ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
              className="p-button-sm p-button-danger"
              onClick={confirmDelete}
              disabled={deleting}
            />
          </div>
        }
      >
        <div style={{ padding: '0.5rem 0', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
          Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>?
          <br />
          <span style={{ color: '#6b7280', fontSize: 13 }}>This action cannot be undone.</span>
        </div>
      </Dialog>
    </div>
  );
}
